import type {
  ComponentDescription,
  ComponentInstance,
  Dolan,
  View,
} from '@sapappgyver/appgyver-descriptors/lib/dolan/v4';
import { getPropertyByPath, setPropertyByPath } from '../../utils/objects';
import getComponentInstanceLogicFlowsFromView from './getComponentInstanceLogicFlowsFromView';
import createComponentInstanceFromDescription from './createComponentInstanceFromDescription';

/**
 * Goes through the given DOLAN and resets all component instances matching
 * the given component description and resets them to match the description state,
 * preserving any customizations like properties, styling etc.
 *
 * @param dolan DOLAN from which to reset the component description instances
 * @returns DOLAN where all component description instances have been reset
 */
export default function resetComponentDescriptionInstances(
  dolan: Dolan,
  description: ComponentDescription,
  ignoredIds?: string[],
): Dolan {
  return {
    ...dolan,
    views: dolan.views.map((view) => resetComponentDescriptionInstancesInView(view, description, ignoredIds)),
  };
}

function resetComponentDescriptionInstancesInView(
  view: View,
  description: ComponentDescription,
  ignoredIds?: string[],
): View {
  return resetComponentDescriptionInstancesInComponent(view, ['layout', 'root_component'], description, ignoredIds);
}

function resetComponentDescriptionInstancesInComponent(
  view: View,
  path: (string | number)[],
  description: ComponentDescription,
  ignoredIds?: string[],
): View {
  const componentInstance = getPropertyByPath(view, path) as ComponentInstance;
  // Check if the current component matches the description
  if (componentInstance.name === description.name) {
    if (ignoredIds?.includes(componentInstance.id)) {
      // Ignore this specific instance => no changes
      return view;
    }
    // Reset this component instance
    return resetComponentDescriptionInstance(view, path, componentInstance, description);
  }
  // Otherwise recursively handle the children
  return componentInstance.children.reduce<View>(
    (updatedView, _child, index) =>
      resetComponentDescriptionInstancesInComponent(updatedView, [...path, 'children', index], description, ignoredIds),
    view,
  );
}

function resetComponentDescriptionInstance(
  view: View,
  path: (string | number)[],
  componentInstance: ComponentInstance,
  description: ComponentDescription,
): View {
  // Remove all old logic flows
  const oldFlows = getComponentInstanceLogicFlowsFromView(componentInstance, view, 'all');
  // Preserve the outer logic for the component instance
  const outerLogicFlows = getComponentInstanceLogicFlowsFromView(componentInstance, view, 'outer');
  const componentId = componentInstance.id;
  const newComponentTemplate = createComponentInstanceFromDescription(description, {
    // Things to preserve from the existing instance
    id: componentId,
    args: componentInstance.args,
    display_name: componentInstance.display_name,
    key: componentInstance.key,
    nested_overrides: componentInstance.nested_overrides,
    repeat: componentInstance.repeat,
    style_class: componentInstance.style_class,
    style: componentInstance.style,
    flows: outerLogicFlows,
    viewId: view.id,
  });
  const newComponentInstance = newComponentTemplate.component;
  const newComponentLogicFlows = newComponentTemplate.flows;
  // Build new logic flows
  const newViewFlows = { ...view.flows };
  // Remove all old logic flows
  oldFlows.forEach((flow) => {
    delete newViewFlows[flow.id];
  });
  // Add the new logic flows
  newComponentLogicFlows.forEach((flow) => {
    newViewFlows[flow.id] = flow;
  });
  // Replace the component instance in the view
  return {
    ...setPropertyByPath(view, path, newComponentInstance),
    flows: newViewFlows,
  };
}
